import { noticeDeadline } from "../contracts/noticeDeadline";
import { tiptapToPlainText } from "../input/TipTap/tiptapToPlainText";

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
type Row = { [key: string]: Json };

export const DESCRIPTION_MAX_CHARS = 2000;

export const NO_NOTICE_PERIOD = "ingen oppsigelsestid er satt";

const NOT_SHOWN_ON_THE_PAGE = new Set([
  "missingDataFields",
  "requiredDataFields",
  "addedDataFields",
  "missingCustomFields",
  "requiredCustomFields",
  "customFields",
  "projects",
  "noticePeriod",
]);

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);

const text = (value: Json | undefined): string | undefined => {
  if (typeof value !== "string") return undefined;
  const plain = value.trimStart().startsWith('{"type":"doc"') ? tiptapToPlainText(value) : value;
  return plain.trim() === "" ? undefined : plain;
};

const personName = (person: Json | undefined): string | undefined => {
  if (!isRow(person)) return undefined;
  const name = [person.firstName, person.lastName].filter((part) => typeof part === "string" && part).join(" ");
  return name || text(person.email);
};

const withoutEmpty = (value: Json): Json | undefined => {
  if (value === null) return undefined;
  if (typeof value === "string") return text(value);
  if (Array.isArray(value)) {
    const items = value.map(withoutEmpty).filter((item): item is Json => item !== undefined);
    return items.length ? items : undefined;
  }
  if (isRow(value)) {
    if ("firstName" in value && "email" in value) return personName(value);
    const entries = Object.entries(value)
      .map(([key, field]) => [key, withoutEmpty(field)] as const)
      .filter((entry): entry is readonly [string, Json] => entry[1] !== undefined);
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  return value;
};

const counterpartyOf = (counterparty: Json | undefined): Json | undefined => {
  if (!isRow(counterparty)) return undefined;
  const company = isRow(counterparty.organization) ? counterparty.organization : counterparty.organizationEntry;
  if (!isRow(company)) return undefined;
  return withoutEmpty({ name: company.name ?? null, organizationNumber: company.organizationNumber ?? null });
};

const customFieldsOf = (sections: Json | undefined): Json[] => {
  if (!isRow(sections)) return [];
  return Object.values(sections).flatMap((section) =>
    isRow(section) && Array.isArray(section.fields)
      ? section.fields.filter(isRow).map((field) => {
          const { type: _type, id: _id, name, isInternal: _internal, options: _options, ...value } = field;
          return withoutEmpty({ section: section.name ?? null, name: name ?? null, ...value }) ?? null;
        })
      : [],
  );
};

const descriptionOf = (description: Json | undefined): string | undefined => {
  const plain = text(description);
  if (!plain || plain.length <= DESCRIPTION_MAX_CHARS) return plain;
  return `${plain.slice(0, DESCRIPTION_MAX_CHARS)}… [beskrivelsen fortsetter på kontraktsiden]`;
};

const noticeOf = (dataFields: Row): Row => {
  const months = dataFields.noticePeriod;
  if (typeof months !== "number" || months <= 0) return { noticePeriod: NO_NOTICE_PERIOD };
  const endDate = isRow(dataFields.endDate) ? text(dataFields.endDate.date) : undefined;
  return { noticePeriodMonths: months, noticeDeadline: endDate ? noticeDeadline(endDate, months) : null };
};

export const contractForModel = (contract: unknown): unknown => {
  if (!isRow(contract)) return contract;
  const dataFields = isRow(contract.dataFields) ? contract.dataFields : {};
  const shownData = Object.fromEntries(Object.entries(dataFields).filter(([key]) => !NOT_SHOWN_ON_THE_PAGE.has(key)));
  const projects = Array.isArray(dataFields.projects)
    ? dataFields.projects.filter(isRow).map((project) => project.name)
    : null;
  const documents = isRow(contract.documentFields) && Array.isArray(contract.documentFields.documents)
    ? contract.documentFields.documents.filter(isRow).map((document) => document.name)
    : null;
  const template = isRow(contract.template) ? contract.template.name : null;
  const department = isRow(contract.department) ? contract.department.name : null;

  const shaped = withoutEmpty({
    id: contract.id ?? null,
    title: contract.title ?? null,
    status: contract.status ?? null,
    state: contract.state ?? null,
    counterparty: counterpartyOf(contract.counterparty) ?? null,
    internalOwner: personName(contract.internalOwner) ?? null,
    department,
    template,
    currency: contract.currency ?? null,
    ...shownData,
    ...(isRow(contract.dataFields) ? noticeOf(contract.dataFields) : {}),
    customFields: customFieldsOf(dataFields.customFields),
    projects,
    frameworkAgreementId: contract.frameworkAgreementId ?? null,
    frameworkRole: contract.frameworkRole ?? null,
    documents,
    description: descriptionOf(contract.description) ?? null,
  });
  return shaped ?? {};
};
